import { AnimatePresence, motion } from 'framer-motion';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { GameV1 } from '@/api/games';
import { ButtonHeart } from '@/components/buttons';
import { isFavoriteGame } from '@/helpers/game';
import { useCommonStore } from '@/store';

import {
  GameItemSize,
  ScGamesGridItemProps,
  ScHearthBgWhite,
} from '../../styled';
import { ScButtonHeartLoader } from '@/components/buttons/styled';

type ButtonHeartComponentProps = Pick<GameV1, 'real'> &
  Pick<ScGamesGridItemProps, 'size'> & {
    isVisible: boolean;
  };

export const ButtonHeartComponent: FC<ButtonHeartComponentProps> = ({
  real,
  size,
  isVisible,
}) => {
  const { favoriteGames, addFavoriteGame, removeFavoriteGame } = useCommonStore(
    useShallow((state) => ({
      favoriteGames: state.favoriteGames,
      addFavoriteGame: state.addFavoriteGame,
      removeFavoriteGame: state.removeFavoriteGame,
    }))
  );

  const isFavorite = useMemo(
    () => isFavoriteGame(real, favoriteGames),
    [real, favoriteGames]
  );

  const [isLoading, setIsLoading] = useState(false);

  const handleFavorite = useCallback(() => {
    setIsLoading(true);

    if (isFavorite) {
      removeFavoriteGame(real);
    } else {
      addFavoriteGame(real);
    }
  }, [isFavorite, addFavoriteGame, removeFavoriteGame, real]);

  useEffect(() => setIsLoading(false), [isFavorite]);

  const heartPosition = useMemo(() => {
    switch (size) {
      case GameItemSize.M:
        return {
          width: 24,
          height: 24,
          top: 8,
          right: 8,
        };
      case GameItemSize.S:
        return {
          width: 24,
          height: 24,
          top: 4,
          right: 4,
        };
      default:
        return {
          width: 24,
          height: 24,
          top: 8,
          right: 8,
        };
    }
  }, [size]);

  return (
    <AnimatePresence>
      <ScHearthBgWhite
        isFavorite={isFavorite}
        isVisible={isVisible}
        size={size}
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.5 } }}
        exit={{ opacity: 0, transition: { duration: 0.3 } }}
      >
        {(isVisible || isFavorite) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.2 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
          >
            {isLoading ? (
              <ScButtonHeartLoader active={isFavorite} {...heartPosition} />
            ) : (
              <div className="btn-heart">
                <ButtonHeart
                  {...heartPosition}
                  active={isFavorite}
                  onClick={handleFavorite}
                />
              </div>
            )}
          </motion.div>
        )}
      </ScHearthBgWhite>
    </AnimatePresence>
  );
};
